import { io, Socket } from 'socket.io-client';
import Cookies from 'js-cookie';
import axiosInstance from '@/api/axiosInstance';

export interface ScrapeJobProgress {
  id: number;
  url: string;
  type: string;
  totalCount: number;
  completedCount: number;
  status: 'in_progress' | 'completed' | 'failed' | 'stopped';
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  category: string | null;
  subCategory: string | null;
  isStopped: boolean;
  code: string | null;
  company: {
    id: string;
    name?: string;
    description?: string;
    avatar?: string;
    email?: string | null;
    phoneNumber?: string;
    imported_countries?: string | null;
    services?: string | null;
    city?: string;
    country?: string;
    website?: string;
    businessType?: string;
  };
  links?: Array<{
    link: string;
    isSuccess: boolean;
    productId?: string;
    error?: string;
  }>;
}

export interface ScrapeJobHistoryResponse {
  data: ScrapeJobProgress[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type ConnectionStatusHandler = (isConnected: boolean) => void;
type JobProgressHandler = (progress: ScrapeJobProgress) => void;
type ScrapeErrorHandler = (error: { message: string }) => void;

const SOCKET_CONFIG = {
  path: '/socket.io',
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 10000,
  transports: ['websocket'] as const,
} as const;

const SOCKET_URL = import.meta.env.VITE_CHAT_SOCKET_URL;

export const createScrapeProductsService = () => {
  let socket: Socket | null = null;
  let onConnectionStatusChange: ConnectionStatusHandler | null = null;
  let onJobProgress: JobProgressHandler | null = null;
  let onScrapeError: ScrapeErrorHandler | null = null;
  let isConnectedState = false;

  const attachListeners = () => {
    if (!socket) return;

    socket.on('connect', () => {
      isConnectedState = true;
      onConnectionStatusChange?.(true);
    });

    socket.on('disconnect', () => {
      isConnectedState = false;
      onConnectionStatusChange?.(false);
    });

    socket.on('connect_error', (err: Error) => {
      isConnectedState = false;
      onConnectionStatusChange?.(false);
      onScrapeError?.({ message: err.message || 'Connection failed' });
    });

    socket.on('jobProgress', (data: unknown) => {
      const progress = data as ScrapeJobProgress;
      onJobProgress?.(progress);
    });

    socket.on('error', (data: unknown) => {
      const error =
        typeof data === 'object' && data !== null && 'message' in data
          ? (data as { message: string })
          : { message: 'Unknown error occurred' };
      onScrapeError?.(error);
    });
  };

  const connectSocket = (handlers: {
    onConnectionStatusChange?: ConnectionStatusHandler;
    onJobProgress?: JobProgressHandler;
    onScrapeError?: ScrapeErrorHandler;
  }) => {
    if (socket && socket.connected) {
      return;
    }

    if (socket) {
      socket.disconnect();
      socket.removeAllListeners();
      socket = null;
    }

    const token = Cookies.get('authToken');
    if (!token) {
      const error = new Error('No authentication token found');
      onScrapeError?.({ message: error.message });
      return;
    }

    socket = io(`${SOCKET_URL}/product`, {
      ...SOCKET_CONFIG,
      auth: {
        token: `Bearer ${token}`,
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      transports: ['websocket'],
    });

    onConnectionStatusChange = handlers.onConnectionStatusChange || null;
    onJobProgress = handlers.onJobProgress || null;
    onScrapeError = handlers.onScrapeError || null;

    attachListeners();
  };

  const disconnectSocket = () => {
    if (!socket) return;
    socket.disconnect();
    socket.removeAllListeners();
    socket = null;
    isConnectedState = false;
    onConnectionStatusChange?.(false);
  };

  const scrapeProducts = (url: string): Promise<void> => {
    if (!socket?.connected) {
      return Promise.reject(new Error('Socket not connected'));
    }

    return new Promise((resolve, reject) => {
      socket?.emit(
        'scrapeProductsWithFirecrawl',
        { url },
        (response: unknown) => {
          if (!response) {
            return;
          }

          const responseObj = response as {
            error?: string;
            success?: boolean;
            jobId?: number;
          };

          if (typeof responseObj !== 'object' || responseObj === null) {
            return;
          }

          if (responseObj?.error) {
            reject(new Error(responseObj.error));
          } else {
            resolve();
          }
        }
      );
    });
  };

  const stopScrapeJob = (jobId: number): Promise<void> => {
    if (!socket?.connected) {
      return Promise.reject(new Error('Socket not connected'));
    }

    return new Promise((resolve, reject) => {
      socket?.emit('stop-scrape-job', { jobId }, (response: unknown) => {
        if (!response) {
          return;
        }

        const responseObj = response as {
          error?: string;
          success?: boolean;
        };

        if (typeof responseObj !== 'object' || responseObj === null) {
          return;
        }

        if (responseObj?.error) {
          reject(new Error(responseObj.error));
        } else {
          resolve();
        }
      });
    });
  };

  return {
    connectSocket,
    disconnectSocket,
    scrapeProducts,
    stopScrapeJob,
    isConnected: () => Boolean(socket?.connected) || isConnectedState,
  };
};

export type ScrapeProductsService = ReturnType<
  typeof createScrapeProductsService
>;

// API function to get all scraping jobs history
export const getAllScrapeJobs = async (
  page: number = 1,
  limit: number = 10
): Promise<ScrapeJobHistoryResponse> => {
  const response = await axiosInstance.get('/products/all-jobs', {
    params: { page, limit },
  });
  return response.data;
};
